/**
 * Question parameters fields component.
 *
 * Renders fields for level, skill, section, and exam types.
 *
 * @module QuestionParametersFields
 */

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExamTypeMultiSelect } from '@/components/ui/exam-type-multi-select';
import { questionTypes, levels, modules } from './constants';
import { getTaskTypeOptionsForModule } from '@/lib/taskTypeOptions';

export const QuestionParametersFields = ({
  level,
  skill,
  taskType,
  section,
  examTypes,
  onChange,
  errors,
  mode = 'single',
  showType = false,
  questionType,
  onExamTypeChange,
}: {
  level: string;
  skill: string;
  taskType?: string | null;
  section: number;
  examTypes: string[];
  onChange: (field: string, value: any) => void;
  errors?: Record<string, string>;
  mode?: 'shared' | 'single';
  showType?: boolean;
  questionType?: string;
  onExamTypeChange?: (examType: string, checked: boolean) => void;
}) => {
  const examTypesErrorKey =
    mode === 'shared' ? 'shared_examTypes' : 'examTypes';
  const examTypesError = errors?.[examTypesErrorKey];
  const taskTypeOptions = getTaskTypeOptionsForModule(skill);
  const taskTypeErrorKey = mode === 'shared' ? 'shared_taskType' : 'taskType';
  const taskTypeError = errors?.[taskTypeErrorKey];

  return (
    <div className="space-y-4">
      {mode === 'single' && showType && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="type" className="font-ui">Question Type</Label>
            <Select
              value={questionType || ''}
              onValueChange={(value) => onChange('type', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {questionTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="level" className="font-ui">CEFR Level</Label>
            <Select
              value={level}
              onValueChange={(value) => onChange('level', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select level" />
              </SelectTrigger>
              <SelectContent>
                {levels.map((lvl) => (
                  <SelectItem key={lvl} value={lvl}>
                    {lvl}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {!(mode === 'single' && showType) && (
        <div className="grid grid-cols-2 gap-4">
          {mode === 'shared' && (
            <div>
              <Label htmlFor="shared-level" className="font-ui">CEFR Level</Label>
              <Select
                value={level}
                onValueChange={(value) => onChange('level', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {levels.map((lvl) => (
                    <SelectItem key={lvl} value={lvl}>
                      {lvl}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label
              htmlFor={mode === 'shared' ? 'shared-module' : 'module'}
              className="font-ui"
            >
              Module
            </Label>
            <Select
              value={skill}
              onValueChange={(value) => onChange('skill', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select module" />
              </SelectTrigger>
              <SelectContent>
                {modules.map((module) => (
                  <SelectItem key={module.value} value={module.value}>
                    {module.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors?.[mode === 'shared' ? 'shared_skill' : 'skill'] && (
              <p className="font-ui text-sm text-destructive mt-1">
                {errors[mode === 'shared' ? 'shared_skill' : 'skill']}
              </p>
            )}
          </div>
        </div>
      )}

      {mode === 'single' && showType && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="module" className="font-ui">Module</Label>
            <Select
              value={skill}
              onValueChange={(value) => onChange('skill', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select module" />
              </SelectTrigger>
              <SelectContent>
                {modules.map((module) => (
                  <SelectItem key={module.value} value={module.value}>
                    {module.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors?.skill && (
              <p className="font-ui text-sm text-destructive mt-1">
                {errors.skill}
              </p>
            )}
          </div>
          {taskTypeOptions.length > 0 && (
            <div>
              <Label htmlFor="taskType" className="font-ui">Task Type</Label>
              <Select
                value={taskType || ''}
                onValueChange={(value) => onChange('taskType', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select task type" />
                </SelectTrigger>
                <SelectContent>
                  {taskTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {taskTypeError && (
                <p className="font-ui text-sm text-destructive mt-1">
                  {taskTypeError}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!(mode === 'single' && showType) && taskTypeOptions.length > 0 && (
        <div>
          <Label
            htmlFor={mode === 'shared' ? 'shared-taskType' : 'taskType'}
            className="font-ui"
          >
            Task Type
          </Label>
          <Select
            value={taskType || ''}
            onValueChange={(value) => onChange('taskType', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select task type" />
            </SelectTrigger>
            <SelectContent>
              {taskTypeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {taskTypeError && (
            <p className="font-ui text-sm text-destructive mt-1">
              {taskTypeError}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label
            htmlFor={mode === 'shared' ? 'shared-section' : 'section'}
            className="font-ui"
          >
            Section
          </Label>
          <Select
            value={section.toString()}
            onValueChange={(value) => onChange('section', Number.parseInt(value))}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select section" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((sect) => (
                <SelectItem key={sect} value={sect.toString()}>
                  Section {sect}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <ExamTypeMultiSelect
            label="Exam Types"
            selectedTypes={examTypes || []}
            onChange={(updatedTypes) => {
              if (mode === 'shared') {
                onChange('examTypes', updatedTypes);
              } else if (onExamTypeChange) {
                const currentTypes = examTypes || [];
                const added = updatedTypes.filter(
                  (t) => !currentTypes.includes(t),
                );
                const removed = currentTypes.filter(
                  (t) => !updatedTypes.includes(t),
                );
                added.forEach((type) => onExamTypeChange(type, true));
                removed.forEach((type) => onExamTypeChange(type, false));
              } else {
                onChange('examTypes', updatedTypes);
              }
            }}
            htmlId={mode === 'shared' ? 'shared-examTypes' : 'examTypes'}
            className={examTypesError ? 'space-y-1' : ''}
          />
          {examTypesError && (
            <p className="font-ui text-sm text-destructive mt-1">
              {examTypesError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
